/**
 * `Positions │ Open Orders │ Fills │ History │ Settled`, and — below 1440px —
 * `Next markets`, which folds in here from the ticket column (components.md §1).
 *
 * The fold is why this takes its tab list rather than hard-coding one: the set
 * genuinely changes with the viewport, and a component that hides a tab with CSS
 * would leave it in the roving tabindex, so a keyboard user would arrow into a
 * panel they cannot see.
 *
 * Row actions inside these tables stay permanently visible below 768px
 * (components.md §3) — a hover-revealed cancel button is unreachable on a touch
 * screen, and cancel is the action a user needs most urgently.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'
import { TabPanel, Tabs } from '@/components/primitives'
import type { TabItem } from '@/components/primitives'

export interface BottomTabsProps<T extends string> {
  items: readonly TabItem<T>[]
  value: T
  onChange: (id: T) => void
  children: ReactNode
  /**
   * Who scrolls. `'panel'`, the default, is the trading page: the tabs fill the
   * fixed bottom row and the rows scroll inside it. `'page'` is a content page
   * (Portfolio): the tabs take their rows' own height — at least the room left
   * below whatever sits above them — and the page scrolls, because on a content
   * page nothing scrolls inside itself but the page (components.md §1). The
   * Fills tab there was 3,461px of rows in a 582px box at 1920×1080.
   */
  scroll?: 'panel' | 'page'
  /** On the active tab's panel — `h-full` for a tab that lays out its own
   *  scroll, as the troll box does with its composer pinned under the log. */
  panelClassName?: string | undefined
  /** At the far end of the tab bar and outside the tablist — a control over
   *  the row rather than one of its tabs, as the troll box's toggle is. */
  trailing?: ReactNode
  className?: string
}

export function BottomTabs<T extends string>({
  items,
  value,
  onChange,
  children,
  scroll = 'panel',
  panelClassName,
  trailing,
  className,
}: BottomTabsProps<T>) {
  return (
    // Growing matters, in both modes: `AppShell` gives the bottom row a fixed
    // height, and a tab bar that sizes to its content leaves canvas showing
    // underneath — which reads as a panel that failed to render rather than as
    // a design.
    <div
      className={cx(
        'flex flex-col bg-bg-surface',
        scroll === 'page' ? 'shrink-0 grow' : 'min-h-0 flex-1',
        className,
      )}
    >
      <div className="relative shrink-0">
        <Tabs items={items} value={value} onChange={onChange} label="portfolio tables" className="px-3" />
        {trailing ? <div className="absolute inset-y-0 right-3 flex">{trailing}</div> : null}
      </div>
      <div className={scroll === 'page' ? undefined : 'ud-scroll min-h-0 flex-1'}>
        <TabPanel id={value} active {...(panelClassName ? { className: panelClassName } : {})}>
          {children}
        </TabPanel>
      </div>
    </div>
  )
}
